import { ADS_BATCH_TEMPLATEIZE_SELECTION_MAX } from "@/lib/adsBatchTemplateizeCore";
import type { useAdsBatchTemplateize } from "@/components/ads-assistant/useAdsBatchTemplateize";
import { excerptForBatch } from "@/components/ads-assistant/adsBatchTemplateizeExcerpt";

export function AdsBatchTemplateizeSelectStep(props: {
  vm: ReturnType<typeof useAdsBatchTemplateize>;
}) {
  const { vm } = props;
  if (vm.loadingPosts) {
    return <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Loading untemplated posts…</p>;
  }
  if (!vm.posts.length) {
    return (
      <p className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-zinc-900/40 dark:text-gray-200">
        No untemplated posts to batch. Every indexed post is already linked to a template.
      </p>
    );
  }
  const remaining = ADS_BATCH_TEMPLATEIZE_SELECTION_MAX - vm.selectedIds.size;
  const firstNCap = Math.min(vm.posts.length, ADS_BATCH_TEMPLATEIZE_SELECTION_MAX);
  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-gray-700 dark:text-gray-300">
          Selected <strong className="tabular-nums">{vm.selectedIds.size}</strong> /{" "}
          {ADS_BATCH_TEMPLATEIZE_SELECTION_MAX}
        </span>
        <button
          type="button"
          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-100"
          onClick={() => vm.selectFirstN(firstNCap)}
        >
          Select first {firstNCap}
        </button>
        <button
          type="button"
          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-100"
          onClick={vm.clearSelection}
        >
          Clear
        </button>
        <span className="text-[11px] text-gray-500 dark:text-gray-400">
          {remaining} more allowed in this batch.
        </span>
      </div>
      <ul className="mt-3 max-h-[50vh] divide-y divide-gray-200 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
        {vm.posts.map((p) => {
          const checked = vm.selectedIds.has(p.id);
          const disabled = !checked && vm.selectedIds.size >= ADS_BATCH_TEMPLATEIZE_SELECTION_MAX;
          return (
            <li key={p.id} className="flex items-start gap-2 px-3 py-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={checked}
                disabled={disabled}
                onChange={() => vm.toggleSelected(p.id)}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-400">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium uppercase dark:bg-zinc-800 dark:text-gray-200">
                    {p.platform}
                  </span>
                  <span className="tabular-nums">{p.published_at?.slice(0, 10) || "—"}</span>
                </div>
                <p className="mt-1 break-words text-xs text-gray-800 dark:text-gray-200">
                  {excerptForBatch(p.text_content) || <em className="text-gray-500">No body text</em>}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-100"
          onClick={vm.closeDialog}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!vm.selectedIds.size}
          className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
          onClick={() => void vm.runSuggestions()}
        >
          Suggest templates for {vm.selectedIds.size || 0}
        </button>
      </div>
    </>
  );
}
